import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from '../../../../localization/useTranslation';
import Tl from '../../../../localization/LocalizationKeys';
import _ from 'lodash';
import ModelType from '../../../Model/ModelType';
import { semanticUnits, PRIMITIVE_SCHEMAS } from '../../../../utils/dtdl';
import ContentTable from '../utils/ContentTable';
import { dtdlNameError } from '../utils/model';
import SelectV2 from 'components/Select/SelectV2';

const columns = [
  {
    label: Tl.model.name,
    path: 'name',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.displayName,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.semanticType,
    path: 'semanticType',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.unit,
    path: 'semanticUnit',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.model.writable,
    path: 'writable',
    isSortable: true,
    render: (val) => (val) ? 'true' : 'false'
  },
  {
    label: Tl.model.schema,
    path: 'schema'
  },
  {
    label: 'Meta Property',
    path: 'metaProperty',
    isSortable: true,
    render: (val) => (val) ? 'true' : 'false'
  }
  // {
  //   label: Tl.model.description,
  //   path: 'description',
  //   searchItem: true
  // },
  // {
  //   label: Tl.model.comment,
  //   path: 'comment',
  //   searchItem: true
  // }
];


export const PropertyTab = ({ properties, onPropertiesUpdate, currentNames, modelDraft }) => {
  const T = useTranslation();
  const [_properties, setProperties] = useState(properties);
  const currentlyEditingItem = useRef({});
  const [content, setContent] = useState({});
  const [filtered, setFiltered] = useState('all');

  const nameHasError = useCallback((name) => {
    return dtdlNameError(name, _.reject(currentNames, n => currentlyEditingItem.current.name === n));
  }, [currentNames]);

  useEffect(() => {
    setProperties(properties);
  }, [properties]);

  const propertiesModel = useMemo(() => {
    const getSemanticUnits = (semanticType) => {
      if (!_.isEmpty(semanticType)) {
        const l = Object.values(semanticUnits[semanticType]);
        return _.map(l, (value) => ({ value, text: value }));
      }
    };
    const allowSemanticType = (type) => (_.includes(['double', 'float', 'integer', 'long'], type) ? true : false);
    const top = [
      {
        type: ModelType.TEXT,
        label: T.model.name,
        path: 'name',
        isRequired: true,
        isReadOnly: modelDraft === false,
        hasError: nameHasError
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.model.schema,
        path: 'schema',
        isRequired: true,
        disabled: modelDraft === false,
        hasError: _.isEmpty,
        options: _.map(PRIMITIVE_SCHEMAS, (value) => ({ value, text: value })),
        properties: {
          isTypeahead: true
        }
      },
      {
        type: ModelType.TEXT,
        label: T.model.displayName,
        path: 'displayName',
        isRequired: true,
        isReadOnly: modelDraft === false,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        label: T.model.description,
        path: 'description',
        isRequired: false,
        isReadOnly: modelDraft === false
      },
      {
        type: ModelType.CHECKBOX,
        label: 'Meta Property',
        path: 'metaProperty',
        disabled: modelDraft === false
      }
    ];
    const semantic = [
      {
        type: ModelType.OPTIONSV2,
        label: T.model.semanticType,
        path: 'semanticType',
        options: _.map(_.keys(semanticUnits), (value) => ({ value, text: value })),
        isRequired: false,
        disabled: modelDraft === false,
        properties: {
          isClearable: true,
          isTypeahead: true
        }
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.model.unit,
        path: 'semanticUnit',
        options: getSemanticUnits(content?.semanticType),
        isRequired: false,
        disabled: modelDraft === false,
        properties: {
          isClearable: true,
          isTypeahead: true
        }
      }
    ];
    return [
      ...top,
      ...(allowSemanticType(content?.schema) ? semantic : []),
      {
        type: ModelType.CHECKBOX,
        label: T.model.writable,
        path: 'writable',
        disabled: modelDraft === false
      },
      {
        type: ModelType.TEXT,
        label: T.model.comment,
        path: 'comment'
      }
    ];

  }, [T.model.comment, T.model.description, T.model.displayName, T.model.name, T.model.schema, T.model.semanticType, T.model.unit, T.model.writable, content?.schema, content?.semanticType, modelDraft, nameHasError]);

  const onSave = useCallback((content) => {
    const cleaned = _.map(content, (t) => (_.isEmpty(t.semanticType)) ? { ...t, semanticUnit: null } : t);
    setProperties(cleaned);
    onPropertiesUpdate(cleaned);
  }, [onPropertiesUpdate]);

  const filteredProperties = useMemo(() => {
    if (filtered === 'asset') {
      return _.filter(_properties, (p) => !p.metaProperty);
    } else if (filtered === 'meta') {
      return _.filter(_properties, (p) => p.metaProperty);
    }
    return _properties;
  }, [_properties, filtered]);

  const globalPropertyActions = useMemo(() => {

    return [
      {
        render: () => {
          return (
            <SelectV2
              options={[{ value: 'all', text: 'All Properties' }, { value: 'meta', text: 'Meta Properties' }, { value: 'asset', text: 'Asset Properties' }]}
              value={filtered}
              onChange={setFiltered}
            />
          );
        }
      }
    ];
  }, [filtered]);

  return (
    <>
      <ContentTable
        title={T.model.tab.properties}
        columns={columns}
        data={filteredProperties}
        dialogModel={propertiesModel}
        updatedContent={setContent}
        onSave={onSave}
        currentlyEditing={item => {
          currentlyEditingItem.current = item;
        }}
        modelDraft={modelDraft}
        globalActions={globalPropertyActions}
      />
    </>
  );
};
